import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { TOUR_STEPS } from './tourSteps';
import '../../styles/OnboardingTour.css';

const SETTING_KEY = 'onboarding_tutorial_completed';
const STEP_KEY = 'onboarding_tour_step';

// Remembers the in-progress step so a page reload (e.g. after creating or
// joining a family) resumes the tour where the user left off.
export const readSavedTourStep = () => {
  try {
    const raw = sessionStorage.getItem(STEP_KEY);
    const parsed = raw === null ? NaN : parseInt(raw, 10);
    if (Number.isInteger(parsed) && parsed >= 0 && parsed < TOUR_STEPS.length) {
      return parsed;
    }
  } catch {
    // sessionStorage may be unavailable; start from the beginning.
  }
  return 0;
};

const getRect = (el) => el.getBoundingClientRect();

export const isTourCompleted = (settings) => {
  try {
    const cached = settings || JSON.parse(localStorage.getItem('userSettings') || '{}');
    return cached[SETTING_KEY] === 'true';
  } catch {
    return true;
  }
};

const OnboardingTour = ({ active, menuOpen, openMenu, onFinish, initialStepIndex = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [stepIndex, setStepIndex] = useState(
    initialStepIndex === null ? readSavedTourStep() : initialStepIndex
  );
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipSide, setTooltipSide] = useState('bottom');
  const [targetMissing, setTargetMissing] = useState(false);
  const stepTimerRef = useRef(null);

  const step = TOUR_STEPS[stepIndex] || null;

  const finish = useCallback(() => {
    try {
      sessionStorage.removeItem(STEP_KEY);
    } catch {
      // Non-fatal.
    }
    onFinish();
  }, [onFinish]);

  // Track the in-progress step so a reload resumes where the user left off.
  useEffect(() => {
    if (!active || !step) return;
    try {
      sessionStorage.setItem(STEP_KEY, String(stepIndex));
    } catch {
      // sessionStorage may be unavailable; non-fatal.
    }
  }, [active, stepIndex, step]);

  // Persist completion to the backend and mirror into the localStorage cache.
  const complete = useCallback(async () => {
    try {
      const cached = JSON.parse(localStorage.getItem('userSettings') || '{}');
      cached[SETTING_KEY] = 'true';
      localStorage.setItem('userSettings', JSON.stringify(cached));
    } catch {
      // localStorage may be unavailable; non-fatal.
    }
    try {
      await api.put(`/usersettings/${SETTING_KEY}`, { value: 'true' });
    } catch {
      // Non-fatal: the localStorage mirror already recorded completion.
    }
    finish();
  }, [finish]);

  // Resolve the current step's target element.
  useEffect(() => {
    if (!active || !step) return undefined;

    if (step.type === 'modal') {
      setTargetRect(null);
      setTargetMissing(false);
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const find = () => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (el && el.isConnected) {
        setTargetRect(getRect(el));
        setTargetMissing(false);
      } else if (attempts < 20) {
        attempts += 1;
        stepTimerRef.current = setTimeout(find, 100);
      } else {
        setTargetMissing(true);
      }
    };
    // Give route transitions / drawer animations a beat to settle.
    stepTimerRef.current = setTimeout(find, 350);
    return () => {
      cancelled = true;
      if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    };
  }, [active, step]);

  // Keep the spotlight aligned on resize/scroll.
  useEffect(() => {
    if (!active || !step || step.type === 'modal' || !targetRect) return undefined;
    const update = () => {
      const el = document.querySelector(step.target);
      if (el) setTargetRect(getRect(el));
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [active, step, targetRect]);

  // Scroll the highlighted element into view.
  useEffect(() => {
    if (!active || !targetRect) return;
    const el = document.querySelector(step.target);
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [active, targetRect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance when the user navigates to the step's destination route.
  useEffect(() => {
    if (!active || !step || step.type !== 'spotlight') return undefined;
    if (!step.advanceOnRoute) return undefined;
    if (location.pathname === step.advanceOnRoute && location.pathname !== step.route) {
      const t = setTimeout(() => setStepIndex((i) => i + 1), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [active, step, location.pathname]);

  // Skip steps whose target is missing (gated menu items, mobile variants).
  useEffect(() => {
    if (!active || !targetMissing) return;
    setTargetMissing(false);
    if (stepIndex >= TOUR_STEPS.length - 1) {
      complete();
    } else {
      setStepIndex((i) => i + 1);
    }
  }, [active, targetMissing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Menu-driven steps open the menu so their target is visible.
  useEffect(() => {
    if (!active || !step || step.type !== 'spotlight' || !step.requiresMenu) return;
    if (!menuOpen) openMenu();
  }, [active, step, menuOpen, openMenu]);

  const goNext = () => {
    if (stepIndex >= TOUR_STEPS.length - 1) {
      complete();
      return;
    }
    // Steps that expect the user to navigate there: route instead of stepping,
    // so the advanceOnRoute auto-advance effect (and any target polling) runs.
    if (step.advanceOnRoute && location.pathname !== step.advanceOnRoute) {
      navigate(step.advanceOnRoute);
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const goBack = () => setStepIndex((i) => Math.max(0, i - 1));

  const skip = () => complete();

  if (!active || !step) return null;

  const progress = `${stepIndex + 1} / ${TOUR_STEPS.length}`;

  if (step.type === 'modal') {
    return (
      <div className="tour-overlay" role="dialog" aria-modal="true" aria-label={step.title}>
        <div className="tour-shield" />
        <div className="tour-modal" data-testid="tour-modal">
          <div className="tour-modal__progress">{progress}</div>
          <h3 className="tour-modal__title">{step.title}</h3>
          <p className="tour-modal__text">{step.text}</p>
          <div className="tour-modal__actions">
            <button type="button" className="btn btn--secondary" onClick={skip}>
              Skip
            </button>
            <button type="button" className="btn btn--primary" onClick={goNext}>
              {step.nextLabel || 'Next'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Spotlight step without a resolved target yet: render nothing until the
  // element appears (or the skip timer flags it as missing).
  if (!targetRect) return null;

  const pad = 6;
  const top = targetRect.top - pad;
  const left = targetRect.left - pad;
  const width = targetRect.width + pad * 2;
  const height = targetRect.height + pad * 2;

  // Prefer the roomier side of the viewport for the tooltip.
  const side = targetRect.top > window.innerHeight / 2 ? 'top' : 'bottom';
  const tooltipTop = side === 'top' ? top - 12 : top + height + 12;
  const tooltipLeft = Math.min(
    Math.max(12, left + width / 2 - 150),
    window.innerWidth - 312
  );

  // Click shield: blocks interaction with the rest of the UI during the tour.
  // For interactive steps the shield is built from four rectangles around the
  // target so only the highlighted element passes clicks through.
  const interactive = !!step.interactive;
  const shieldParts = interactive
    ? [
        { top: 0, left: 0, width: '100%', height: Math.max(0, top) },
        { top: top + height, left: 0, width: '100%', height: Math.max(0, window.innerHeight - (top + height)) },
        { top, left: 0, width: Math.max(0, left), height },
        { top, left: left + width, width: Math.max(0, window.innerWidth - (left + width)), height },
      ]
    : null;
  const shield = shieldParts
    ? shieldParts.map((part, i) => (
        <div
          key={i}
          className="tour-shield tour-shield__part"
          style={part}
        />
      ))
    : <div className="tour-shield" />;

  return (
    <div className="tour-overlay" role="dialog" aria-modal="true" aria-label={step.title}>
      {shield}
      <div
        className="tour-spotlight"
        style={{ top, left, width, height }}
        data-testid="tour-spotlight"
      />
      <div
        className={`tour-tooltip tour-tooltip--${side}`}
        style={{ top: tooltipTop, left: tooltipLeft }}
        data-testid="tour-tooltip"
      >
        <div className="tour-tooltip__progress">{progress}</div>
        <h4 className="tour-tooltip__title">{step.title}</h4>
        <p className="tour-tooltip__text">{step.text}</p>
        <div className="tour-tooltip__actions">
          <button type="button" className="btn btn--secondary" onClick={skip}>
            Skip
          </button>
          <div className="tour-tooltip__nav">
            {stepIndex > 0 && (
              <button type="button" className="btn btn--secondary" onClick={goBack}>
                Back
              </button>
            )}
            <button type="button" className="btn btn--primary" onClick={goNext}>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;